import {
  App,
  Button,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { CheckCircleOutlined, EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  frontDeskApi,
  type Complaint,
  type ComplaintInput,
  type ComplaintPriority,
  type ComplaintStatus,
} from '@/api/frontdesk';
import { studentsApi } from '@/api/students';
import { hrApi } from '@/api/hr';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const PRIORITY_COLOR: Record<ComplaintPriority, string> = {
  LOW: 'default',
  MEDIUM: 'gold',
  HIGH: 'red',
};
const STATUS_COLOR: Record<ComplaintStatus, string> = {
  OPEN: 'processing',
  IN_PROGRESS: 'cyan',
  RESOLVED: 'success',
  CLOSED: 'default',
};
const STATUS_LABEL: Record<ComplaintStatus, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

const CATEGORIES = ['ACADEMIC', 'DISCIPLINE', 'FEE', 'FACILITY', 'OTHER'] as const;

interface FormShape {
  category: string;
  subject: string;
  description: string;
  priority: ComplaintPriority;
  complainantName?: string;
  complainantPhone?: string;
  studentId?: number;
  assignedToStaffId?: number;
}

export function ComplaintsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('FRONT_DESK:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('FRONT_DESK:EDIT'));

  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(60, 'day'), dayjs()]);
  const [statusFilter, setStatusFilter] = useState<ComplaintStatus | undefined>(undefined);
  const [priorityFilter, setPriorityFilter] = useState<ComplaintPriority | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [editing, setEditing] = useState<Complaint | null>(null);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<Complaint | null>(null);
  const [form] = Form.useForm<FormShape>();

  const params = {
    from: range[0].format('YYYY-MM-DD'),
    to: range[1].format('YYYY-MM-DD'),
    status: statusFilter,
    priority: priorityFilter,
    q: search.trim() || undefined,
  };

  const { data, isFetching } = useQuery({
    queryKey: ['frontdesk', 'complaints', params],
    queryFn: () => frontDeskApi.listComplaints(params),
  });

  const { data: studentResults } = useQuery({
    queryKey: ['students', 'search', studentSearch],
    queryFn: () => studentsApi.search({ q: studentSearch.trim() || undefined, size: 15 }),
    enabled: studentSearch.trim().length > 0,
  });

  const { data: staffPage } = useQuery({
    queryKey: ['hr', 'staff', 'active', 'all'],
    queryFn: () => hrApi.searchStaff({ active: true, size: 500 }),
    staleTime: 60_000,
  });
  const staff = staffPage?.rows ?? [];

  const upsert = useMutation({
    mutationFn: (values: FormShape) => {
      const body: ComplaintInput = {
        category: values.category,
        subject: values.subject.trim(),
        description: values.description.trim(),
        priority: values.priority,
        complainantName: values.complainantName?.trim() || undefined,
        complainantPhone: values.complainantPhone?.trim() || undefined,
        studentId: values.studentId ?? null,
        assignedToStaffId: values.assignedToStaffId ?? null,
      };
      return editing ? frontDeskApi.updateComplaint(editing.id, body) : frontDeskApi.createComplaint(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Complaint updated' : 'Complaint lodged');
      qc.invalidateQueries({ queryKey: ['frontdesk', 'complaints'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const updateStatus = useMutation({
    mutationFn: (args: {
      id: number;
      status: ComplaintStatus;
      assignedToStaffId?: number | null;
      resolutionNotes?: string;
    }) =>
      frontDeskApi.updateComplaintStatus(args.id, {
        status: args.status,
        assignedToStaffId: args.assignedToStaffId,
        resolutionNotes: args.resolutionNotes,
      }),
    onSuccess: (c) => {
      message.success(`Marked ${STATUS_LABEL[c.status].toLowerCase()}`);
      qc.invalidateQueries({ queryKey: ['frontdesk', 'complaints'] });
      setViewing(c);
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ priority: 'MEDIUM', category: 'ACADEMIC' });
  };
  const openEdit = (row: Complaint) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      category: row.category,
      subject: row.subject,
      description: row.description,
      priority: row.priority,
      complainantName: row.complainantName ?? undefined,
      complainantPhone: row.complainantPhone ?? undefined,
      studentId: row.studentId ?? undefined,
      assignedToStaffId: row.assignedToStaffId ?? undefined,
    });
  };
  const open = creating || editing != null;

  const studentOptions =
    studentResults?.content.map((s) => ({
      value: s.id,
      label: `${s.firstName} ${s.lastName} · ${s.registrationNumber}`,
    })) ?? [];

  const columns: TableProps<Complaint>['columns'] = [
    {
      title: 'Logged',
      dataIndex: 'createdAt',
      width: 120,
      render: (t: string) => dayjs(t).format('D MMM · HH:mm'),
    },
    {
      title: 'Subject',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.subject}</Typography.Text>
          <Space size={6}>
            <Tag>{row.category}</Tag>
            {row.studentName && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.studentName} · {row.studentRegistrationNumber}
              </Typography.Text>
            )}
          </Space>
        </Space>
      ),
    },
    {
      title: 'From',
      width: 200,
      render: (_, row) =>
        row.complainantName || row.complainantPhone ? (
          <Space orientation="vertical" size={0}>
            {row.complainantName && <Typography.Text>{row.complainantName}</Typography.Text>}
            {row.complainantPhone && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.complainantPhone}
              </Typography.Text>
            )}
          </Space>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      width: 100,
      render: (p: ComplaintPriority) => <Tag color={PRIORITY_COLOR[p]}>{p}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      render: (s: ComplaintStatus) => <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s]}</Tag>,
    },
    {
      title: 'Assigned',
      dataIndex: 'assignedToStaffName',
      width: 160,
      render: (n: string | null) => n ?? <span style={{ opacity: 0.4 }}>—</span>,
    },
    {
      title: 'Actions',
      width: 130,
      align: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Tooltip title="View / resolve">
            <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => setViewing(row)} />
          </Tooltip>
          {canEdit && row.status !== 'CLOSED' && (
            <Tooltip title="Edit details">
              <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Front Desk"
        title="Complaints"
        description="Lodged concerns from parents, students, and staff. Assign and resolve."
        count={data ? { value: data.length, label: 'in range' } : undefined}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New complaint
            </Button>
          ) : null
        }
      />

      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
        <Select
          allowClear
          placeholder="Status"
          style={{ minWidth: 160 }}
          value={statusFilter}
          onChange={(v) => setStatusFilter(v)}
          options={(Object.keys(STATUS_LABEL) as ComplaintStatus[]).map((k) => ({
            value: k,
            label: STATUS_LABEL[k],
          }))}
        />
        <Select
          allowClear
          placeholder="Priority"
          style={{ minWidth: 130 }}
          value={priorityFilter}
          onChange={(v) => setPriorityFilter(v)}
          options={(['LOW', 'MEDIUM', 'HIGH'] as ComplaintPriority[]).map((p) => ({ value: p, label: p }))}
        />
        <Input.Search
          allowClear
          placeholder="Search subject / category / name"
          style={{ width: 280 }}
          onSearch={(v) => setSearch(v)}
        />
      </Space>

      {data && data.length === 0 && !isFetching ? (
        <Empty description="No complaints in this view" />
      ) : (
        <Table
          rowKey="id"
          size="middle"
          loading={isFetching}
          columns={columns}
          dataSource={data ?? []}
          pagination={{ pageSize: 25 }}
        />
      )}

      <Drawer
        title={editing ? `Edit · ${editing.subject}` : 'New complaint'}
        open={open}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        size={520}
        destroyOnHidden
        extra={
          <Space>
            <Button
              onClick={() => {
                setEditing(null);
                setCreating(false);
              }}
            >
              Cancel
            </Button>
            <Button type="primary" loading={upsert.isPending} onClick={() => form.submit()}>
              {editing ? 'Save' : 'Create'}
            </Button>
          </Space>
        }
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                label="Category"
                name="category"
                rules={[{ required: true }]}
                style={{ width: 200 }}
              >
                <Select options={CATEGORIES.map((c) => ({ value: c, label: c }))} />
              </Form.Item>
              <Form.Item
                label="Priority"
                name="priority"
                rules={[{ required: true }]}
                style={{ flex: 1 }}
              >
                <Select
                  options={(['LOW', 'MEDIUM', 'HIGH'] as ComplaintPriority[]).map((p) => ({
                    value: p,
                    label: p,
                  }))}
                />
              </Form.Item>
            </Space.Compact>
            <Form.Item label="Subject" name="subject" rules={[{ required: true, max: 300 }]}>
              <Input autoFocus placeholder="One-line summary" />
            </Form.Item>
            <Form.Item label="Description" name="description" rules={[{ required: true, max: 5000 }]}>
              <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} />
            </Form.Item>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                label="Complainant"
                name="complainantName"
                style={{ flex: 1 }}
                rules={[{ max: 200 }]}
              >
                <Input placeholder="Name (optional)" />
              </Form.Item>
              <Form.Item
                label="Phone"
                name="complainantPhone"
                style={{ width: 200 }}
                rules={[{ max: 20 }]}
              >
                <Input placeholder="03xx-xxxxxxx" />
              </Form.Item>
            </Space.Compact>
            <Form.Item label="Related student (optional)" name="studentId">
              <Select
                allowClear
                showSearch
                placeholder="Search by name or registration number"
                filterOption={false}
                onSearch={(v) => setStudentSearch(v)}
                notFoundContent={studentSearch.trim() === '' ? 'Type to search…' : 'No matches'}
                options={studentOptions}
              />
            </Form.Item>
            <Form.Item label="Assign to (optional)" name="assignedToStaffId">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={staff.map((s) => ({ value: s.id, label: `${s.firstName} ${s.lastName}` }))}
              />
            </Form.Item>
          </Form>
        )}
      </Drawer>

      {viewing && (
        <ComplaintDetailDrawer
          complaint={viewing}
          staff={staff.map((s) => ({ value: s.id, label: `${s.firstName} ${s.lastName}` }))}
          onClose={() => setViewing(null)}
          onUpdate={(args) => updateStatus.mutate({ id: viewing.id, ...args })}
          updating={updateStatus.isPending}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}

function ComplaintDetailDrawer({
  complaint,
  staff,
  onClose,
  onUpdate,
  updating,
  canEdit,
}: {
  complaint: Complaint;
  staff: { value: number; label: string }[];
  onClose: () => void;
  onUpdate: (args: {
    status: ComplaintStatus;
    assignedToStaffId?: number | null;
    resolutionNotes?: string;
  }) => void;
  updating: boolean;
  canEdit: boolean;
}) {
  const [assigned, setAssigned] = useState<number | undefined>(complaint.assignedToStaffId ?? undefined);
  const [notes, setNotes] = useState(complaint.resolutionNotes ?? '');

  return (
    <Drawer open onClose={onClose} title={complaint.subject} size={520}>
      <Space orientation="vertical" style={{ width: '100%' }} size={12}>
        <Space>
          <Tag>{complaint.category}</Tag>
          <Tag color={PRIORITY_COLOR[complaint.priority]}>{complaint.priority}</Tag>
          <Tag color={STATUS_COLOR[complaint.status]}>{STATUS_LABEL[complaint.status]}</Tag>
        </Space>
        <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
          {complaint.description}
        </Typography.Paragraph>
        {(complaint.complainantName || complaint.complainantPhone) && (
          <Typography.Text type="secondary">
            From: {complaint.complainantName ?? '—'}
            {complaint.complainantPhone ? ` · ${complaint.complainantPhone}` : ''}
          </Typography.Text>
        )}
        {complaint.studentName && (
          <Typography.Text type="secondary">
            Re student: {complaint.studentName} · {complaint.studentRegistrationNumber}
          </Typography.Text>
        )}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Logged {dayjs(complaint.createdAt).format('D MMM YYYY · HH:mm')}
          {complaint.resolvedAt && ` · Resolved ${dayjs(complaint.resolvedAt).format('D MMM YYYY · HH:mm')}`}
        </Typography.Text>

        <div style={{ height: 1, background: 'var(--ant-color-border-secondary)' }} />

        <Typography.Text strong>Assignment & resolution</Typography.Text>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Assign to a staff member"
          value={assigned}
          onChange={(v) => setAssigned(v)}
          options={staff}
          style={{ width: '100%' }}
          disabled={!canEdit}
        />
        <Input.TextArea
          placeholder="Resolution notes (required when resolving / closing)"
          autoSize={{ minRows: 3, maxRows: 6 }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={!canEdit}
        />
        {canEdit && (
          <Space wrap>
            <Button
              loading={updating}
              onClick={() => onUpdate({ status: 'IN_PROGRESS', assignedToStaffId: assigned ?? null })}
            >
              Mark in progress
            </Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={updating}
              disabled={!notes.trim()}
              onClick={() =>
                onUpdate({
                  status: 'RESOLVED',
                  assignedToStaffId: assigned ?? null,
                  resolutionNotes: notes.trim(),
                })
              }
            >
              Resolve
            </Button>
            <Button
              danger
              loading={updating}
              disabled={!notes.trim()}
              onClick={() =>
                onUpdate({
                  status: 'CLOSED',
                  assignedToStaffId: assigned ?? null,
                  resolutionNotes: notes.trim(),
                })
              }
            >
              Close
            </Button>
          </Space>
        )}
      </Space>
    </Drawer>
  );
}
